import { doc, getDoc, setDoc, runTransaction } from "firebase/firestore";
import { db } from "../../../index";
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import Spinner from "../../../spinner";

// Type labels for displaying node types in Hebrew
const typeLabels = {
    nose: "נושא",
    helek: "חלק",
    kereh: "כרך",
    siman: "סימן",
    saif: "סעיף",
    perek: "פרק",
    daf: "דף",
    mizmor: "מזמור",
    tora: "תורה",
    shaar: "שער",
    masehet: "מסכת",
    sefer: "ספר",
    halahot: "הלכות",
    seder: "סדר",
    parashat: "פרשת",
    mishna: "משנה",
    alia: "עליה"
};

// Helper function to get all leaf node IDs from a subtree
const getLeafNodeIds = (node) => {
    if (!node.children || node.children.length === 0) {
        return [node.id];
    }
    let ids = [];
    node.children.forEach(child => {
        ids = ids.concat(getLeafNodeIds(child));
    });
    return ids;
};

// Tree selection node component for editing
const SelectionTreeNode = ({
    node,
    depth = 0,
    selectedPieces,
    takenPiecesMap,
    userOriginalPieces,
    onToggleSelection,
    treeData
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const hasChildren = node.children && node.children.length > 0;
    const isLeaf = !hasChildren;

    // Check if this leaf belongs to current user (from original selection)
    const isUserOriginalPiece = isLeaf && userOriginalPieces.includes(node.id);

    // Check if this leaf is taken (by someone else, not by current user)
    const isTakenByOthers = isLeaf && takenPiecesMap[node.id] && !isUserOriginalPiece;

    // Check if this leaf is selected by current user
    const isSelected = isLeaf && selectedPieces.includes(node.id);

    // For parent nodes, check selection status
    const getSelectionStatus = () => {
        if (isLeaf) {
            return {
                allSelected: isSelected,
                someSelected: isSelected,
                allTaken: isTakenByOthers
            };
        }

        const leafIds = getLeafNodeIds(node);
        // Available = not taken by others (user's own pieces are available)
        const availableLeafs = leafIds.filter(id => !takenPiecesMap[id] || userOriginalPieces.includes(id));
        const selectedLeafs = leafIds.filter(id => selectedPieces.includes(id));
        const takenByOthersLeafs = leafIds.filter(id => takenPiecesMap[id] && !userOriginalPieces.includes(id));

        return {
            allSelected: availableLeafs.length > 0 && availableLeafs.every(id => selectedPieces.includes(id)),
            someSelected: selectedLeafs.length > 0,
            allTaken: takenByOthersLeafs.length === leafIds.length,
            availableCount: availableLeafs.length,
            selectedCount: selectedLeafs.length,
            totalCount: leafIds.length,
            takenCount: takenByOthersLeafs.length
        };
    };

    const status = getSelectionStatus();

    // Handle selection of parent (select all available children)
    const handleParentSelection = () => {
        if (status.allTaken) return;

        const leafIds = getLeafNodeIds(node);
        const availableLeafs = leafIds.filter(id => !takenPiecesMap[id] || userOriginalPieces.includes(id));

        if (status.allSelected) {
            // Deselect all
            availableLeafs.forEach(id => {
                if (selectedPieces.includes(id)) {
                    onToggleSelection(id);
                }
            });
        } else {
            // Select all available
            availableLeafs.forEach(id => {
                if (!selectedPieces.includes(id)) {
                    onToggleSelection(id);
                }
            });
        }
    };

    const getNodeDisplayName = () => {
        let name = node.name || '';
        if (node.type === 'daf' && node.dafFrom && node.dafTo) {
            name += ` (${node.dafFrom} - ${node.dafTo})`;
        }
        return name;
    };

    const getHeaderClass = () => {
        let cls = 'aher-selection-node-header';
        if (isLeaf) {
            cls += ' aher-selection-leaf';
            if (isTakenByOthers) cls += ' aher-selection-taken';
            else if (isSelected) cls += ' aher-selection-selected';
        } else {
            cls += ' aher-selection-parent';
            if (status.allTaken) cls += ' aher-selection-taken';
            else if (status.allSelected) cls += ' aher-selection-selected';
            else if (status.someSelected) cls += ' aher-selection-partial';
        }
        return cls;
    };

    return (
        <div className={`aher-selection-node ${hasChildren ? 'aher-selection-node-parent' : ''}`} style={{ marginRight: `${depth * 15}px` }} data-depth={depth}>
            <div className={getHeaderClass()}>
                {hasChildren && (
                    <span
                        className={`aher-selection-expand-icon ${isExpanded ? 'expanded' : ''}`}
                        onClick={() => {setIsExpanded(!isExpanded);}}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </span>
                )}

                {isLeaf ? (
                    <label className="aher-selection-checkbox-container">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isTakenByOthers}
                            onChange={() => onToggleSelection(node.id)}
                        />
                        <span className="aher-selection-checkmark"></span>
                    </label>
                ) : (
                    <label className="aher-selection-checkbox-container">
                        <input
                            type="checkbox"
                            checked={status.allSelected}
                            disabled={status.allTaken}
                            onChange={handleParentSelection}
                            ref={el => {
                                if (el) {
                                    el.indeterminate = status.someSelected && !status.allSelected;
                                }
                            }}
                        />
                        <span className="aher-selection-checkmark"></span>
                    </label>
                )}

                <span className="aher-selection-type-badge">{typeLabels[node.type] || node.type}</span>
                <span className="aher-selection-node-name">{getNodeDisplayName()}</span>

                {isLeaf && isTakenByOthers && (
                    <span className="aher-selection-taken-badge">נתפס</span>
                )}

                {isLeaf && isUserOriginalPiece && isSelected && (
                    <span className="badge bg-info" style={{ marginRight: '5px', fontSize: '10px' }}>שלך</span>
                )}

                {!isLeaf && (
                    <span className="aher-selection-stats">
                        <span className="badge bg-secondary">{status.totalCount - status.takenCount - status.selectedCount} פנויים</span>
                        {status.selectedCount > 0 && <span className="badge bg-primary">{status.selectedCount}/{status.availableCount} נבחרו</span>}
                    </span>
                )}
            </div>

            {hasChildren && isExpanded && (
                <div className="aher-selection-children">
                    {node.children.map((child, idx) => (
                        <SelectionTreeNode
                            key={child.id || idx}
                            node={child}
                            depth={depth + 1}
                            selectedPieces={selectedPieces}
                            takenPiecesMap={takenPiecesMap}
                            userOriginalPieces={userOriginalPieces}
                            onToggleSelection={onToggleSelection}
                            treeData={treeData}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const EditUserAher = () => {
    const params = useParams();
    const navigate = useNavigate();

    // User data
    const [currUser, setCurrUser] = useState(null);
    const [userOriginalPieces, setUserOriginalPieces] = useState([]);

    // Sium and mapping data
    const [sium, setSium] = useState(null);
    const [mappingData, setMappingData] = useState(null);

    // User form data
    const [fullname, setFullname] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [nishmat, setNishmat] = useState('');

    // Selection state
    const [selectedPieces, setSelectedPieces] = useState([]);

    // UI state
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [haveChanges, setHaveChanges] = useState(false);
    const [havePiecesChanges, setHavePiecesChanges] = useState(false);
    const [showNishmatInput, setShowNishmatInput] = useState(false);

    // Create a map of taken pieces for quick lookup
    const takenPiecesMap = useMemo(() => {
        const map = {};
        if (sium?.learningPieces) {
            sium.learningPieces.forEach(piece => {
                if (piece.taken) {
                    map[piece.id] = true;
                }
            });
        }
        return map;
    }, [sium?.learningPieces]);

    // Load user, sium and mapping data
    useEffect(() => {
        const fetchData = async () => {
            try {
                // First, fetch the user
                const userRef = doc(db, "usersAher", params.userId);
                const userDoc = await getDoc(userRef);

                if (!userDoc.exists()) {
                    navigate("/");
                    return;
                }

                const userData = userDoc.data();
                setCurrUser(userData);
                setFullname(userData.fullName || '');
                setPhone(userData.phone || '');
                setEmail(userData.email || '');
                setNishmat(userData.nishmat || '');
                if (userData.nishmat) {
                    setShowNishmatInput(true);
                }

                // Get original pieces from user
                const originalPieces = Object.keys(userData.pieces || {});
                setUserOriginalPieces(originalPieces);
                setSelectedPieces(originalPieces);

                // Fetch the sium
                const siumRef = doc(db, "siumimAher", userData.siumId);
                const siumDoc = await getDoc(siumRef);

                if (siumDoc.exists()) {
                    const siumData = siumDoc.data();
                    setSium(siumData);

                    // Fetch mapping data
                    if (siumData.mipuiId) {
                        const mappingRef = doc(db, "mipui", siumData.mipuiId);
                        const mappingDoc = await getDoc(mappingRef);
                        if (mappingDoc.exists()) {
                            setMappingData(mappingDoc.data());
                        }
                    }
                } else {
                    navigate("/");
                }
            } catch (e) {
                console.error("Error fetching data:", e);
                navigate("/");
            }
        };

        fetchData();
    }, [params.userId, navigate]);

    // Toggle selection of a piece
    const handleToggleSelection = (pieceId) => {
        setHaveChanges(true);
        setHavePiecesChanges(true);
        setSelectedPieces(prev => {
            if (prev.includes(pieceId)) {
                return prev.filter(id => id !== pieceId);
            } else {
                return [...prev, pieceId];
            }
        });
    };

    // Display error message
    const displayErrorMessage = (reset, errorMsg) => {
        if (reset) {
            setSelectedPieces([...userOriginalPieces]);
        }
        setErrorMessage(errorMsg);
        const toastModal = document.getElementById('errorModal');
        if (toastModal) {
            const toastBootstrap = window.bootstrap.Toast.getOrCreateInstance(toastModal);
            toastBootstrap.show();
        }
    };

    // Validate and update sium with transaction
    const validateAndUpdateSium = async () => {
        const siumDocRef = doc(db, "siumimAher", currUser.siumId);
        setErrorMessage("");

        try {
            return await runTransaction(db, async (transaction) => {
                const siumDoc = await transaction.get(siumDocRef);

                if (!siumDoc.exists()) {
                    displayErrorMessage(true, "הסיום לא נמצא");
                    return null;
                }

                const siumObj = siumDoc.data();
                setSium(siumObj);

                if (!havePiecesChanges) {
                    // No pieces changes, just return the sium
                    return siumObj;
                }

                // Check if selected pieces are still available
                const learningPieces = siumObj.learningPieces || [];
                const learningPiecesMap = {};
                learningPieces.forEach(piece => {
                    learningPiecesMap[piece.id] = piece;
                });

                if (selectedPieces.length === 0) {
                    displayErrorMessage(false, "לא נבחרו חלקים ללימוד");
                    return null;
                }

                // Validate new pieces are available (pieces that are not in user's original selection)
                for (const pieceId of selectedPieces) {
                    const piece = learningPiecesMap[pieceId];
                    if (!piece) {
                        displayErrorMessage(true, "אחד החלקים שנבחרו לא קיים");
                        return null;
                    }
                    // Check if piece is taken by someone else (not by this user)
                    if (piece.taken && !userOriginalPieces.includes(pieceId)) {
                        displayErrorMessage(true, "אחד החלקים שנבחרו כבר נתפס, נסה לבחור שוב");
                        return null;
                    }
                }

                // Update learning pieces:
                // 1. Mark newly selected pieces as taken
                // 2. Mark removed pieces (that were in original but not in new selection) as not taken
                siumObj.learningPieces = learningPieces.map(piece => {
                    const wasOriginallySelected = userOriginalPieces.includes(piece.id);
                    const isNowSelected = selectedPieces.includes(piece.id);

                    if (isNowSelected && !wasOriginallySelected) {
                        // Newly selected - mark as taken
                        return { ...piece, taken: true };
                    } else if (!isNowSelected && wasOriginallySelected) {
                        // Removed from selection - mark as not taken (and not learned)
                        return { ...piece, taken: false, learned: false };
                    }
                    return piece;
                });

                siumObj.lastAccessed = new Date().getTime();
                transaction.update(siumDocRef, siumObj);

                return siumObj;
            });
        } catch (e) {
            console.error("Transaction failed:", e);
            displayErrorMessage(false, "ישנו כעת עומס בבחירת החלקים, נסה בעוד מספר שניות");
            return null;
        }
    };

    // Handle form submission
    const handleSubmit = async (event) => {
        event.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const updatedSium = await validateAndUpdateSium();

            if (updatedSium) {
                // Preserve learned status for pieces that were already learned
                const newPieces = {};
                selectedPieces.forEach(pieceId => {
                    // If piece was in original selection and was learned, keep learned status
                    if (currUser.pieces && currUser.pieces[pieceId] !== undefined) {
                        newPieces[pieceId] = currUser.pieces[pieceId];
                    } else {
                        newPieces[pieceId] = false; // New piece, not learned yet
                    }
                });

                const cleanedEmail = email ? String(email).trim().toLowerCase() : '';
                // Update user document
                const updatedUserObj = {
                    email: cleanedEmail,
                    phone: phone.replace(/\D/g, ''),
                    fullName: fullname,
                    nishmat: nishmat,
                    siumId: currUser.siumId,
                    mipuiId: sium.mipuiId,
                    siumType: 'aher',
                    lastModified: new Date(),
                    pieces: newPieces,
                    piecesCount: selectedPieces.length
                };

                const userDocRef = doc(db, "usersAher", params.userId);
                await setDoc(userDocRef, updatedUserObj);

                // Navigate back to sium status
                navigate("/niulSiumAher/" + currUser.siumId + "/siumStatus");
            } else {
                if (!errorMessage) {
                    displayErrorMessage(false, "ישנו כעת עומס בבחירת החלקים, נסה בעוד מספר שניות");
                }
            }
        } catch (e) {
            console.error("Error updating user:", e);
            displayErrorMessage(false, "שגיאה בעדכון הלומד");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle phone change - only allow digits
    const handlePhoneChange = (e) => {
        const numericValue = e.target.value.replace(/\D/g, '');
        setPhone(numericValue);
        setHaveChanges(true);
    };

    // Handle other field changes
    const handleFieldChange = (setter) => (e) => {
        setter(e.target.value);
        setHaveChanges(true);
    };

    // Loading state
    if (!currUser || !sium || !mappingData) {
        return <Spinner />;
    }

    const bookName = mappingData?.bookName || "ספר";

    return (
        <div className="sium-main">
            <div className="sium-main-container">
                <div className="basad-with-return">
                    <div className="basad">
                        <span>בס"ד</span>
                        <br/>
                        <span>להצלחת עם ישראל</span>
                    </div>
                    <div className="return-container">
                        <a className="btn sium-button return-button"
                           href={"/niulSiumAher/" + currUser.siumId + "/siumStatus"}>חזור</a>
                    </div>
                </div>

                <div className="sium-logo">
                    <a className="sium-logo-img" href="/" aria-label="Main page">
                        <span className="visually-hidden">דף הבית</span>
                    </a>
                </div>

                <div className="grid p-3 text-primary-emphasis bg-primary-subtle border border-primary-subtle rounded-3 sium-titles">
                    {sium.nishmat && <span>הלימוד {sium.nishmat}</span>}
                    <h2>חלוקת {bookName} של {sium.kehilaName}</h2>
                    <h4>עריכת לומד: {currUser.fullName}</h4>
                    {sium.logo && <div className={sium.logo} role="img" aria-label="לוגו"/>}
                </div>

                <form onSubmit={handleSubmit}>
                    <input
                        className="form-control"
                        type="text"
                        placeholder="שם מלא"
                        name="fullName"
                        value={fullname}
                        onChange={handleFieldChange(setFullname)}
                        aria-label="Full name"
                        required={true}
                    />
                    <input
                        className="form-control"
                        type="text"
                        placeholder="פלאפון"
                        name="phone"
                        value={phone}
                        onChange={handlePhoneChange}
                        aria-label="Phone"
                    />
                    <input
                        className="form-control"
                        type="text"
                        placeholder="אימייל"
                        name="email"
                        value={email}
                        onChange={handleFieldChange(setEmail)}
                        aria-label="Email"
                    />

                    {!showNishmatInput ? (
                        <div className="form-check nishmat-checkbox">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="showNishmatCheckbox"
                                onChange={() => setShowNishmatInput(true)}
                            />
                            <label className="form-check-label" htmlFor="showNishmatCheckbox">
                                להוסיף לעילוי נשמת/לרפואת/להצלחת
                            </label>
                        </div>
                    ) : (
                        <input
                            className="form-control"
                            type="text"
                            placeholder="לעילוי נשמת/לרפואת/להצלחת"
                            name="nishmat"
                            value={nishmat}
                            onChange={handleFieldChange(setNishmat)}
                            aria-label="Nishmat"
                        />
                    )}

                    {/* Book selection tree */}
                    <div className="aher-selection-container">
                        <div className="aher-selection-header">
                            <h5>בחר חלקים ללימוד</h5>
                            <span className="aher-selection-count">
                                {selectedPieces.length} חלקים נבחרו
                            </span>
                        </div>

                        <div className="aher-selection-legend">
                            <div className="aher-legend-item">
                                <span className="aher-legend-box aher-legend-available"></span>
                                <span>פנוי</span>
                            </div>
                            <div className="aher-legend-item">
                                <span className="aher-legend-box aher-legend-selected"></span>
                                <span>נבחר</span>
                            </div>
                            <div className="aher-legend-item">
                                <span className="aher-legend-box aher-legend-taken"></span>
                                <span>נתפס</span>
                            </div>
                        </div>

                        <div className="aher-selection-tree">
                            {mappingData.treeData && mappingData.treeData.map((node, idx) => (
                                <SelectionTreeNode
                                    key={node.id || idx}
                                    node={node}
                                    depth={0}
                                    selectedPieces={selectedPieces}
                                    takenPiecesMap={takenPiecesMap}
                                    userOriginalPieces={userOriginalPieces}
                                    onToggleSelection={handleToggleSelection}
                                    treeData={mappingData.treeData}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Error toast */}
                    <div className="toast-container position-fixed top-0 start-50 translate-middle-x">
                        <div id="errorModal" className="toast" role="alert" aria-live="assertive"
                             aria-atomic="true" data-bs-autohide="true" data-bs-delay="3000">
                            <button type="button" className="btn-close me-2 m-auto" data-bs-dismiss="toast"
                                    aria-label="Close"></button>
                            <div className="toast-body">
                                {errorMessage}
                            </div>
                        </div>
                    </div>

                    {/* Submit button */}
                    {haveChanges && selectedPieces.length > 0 && !isSubmitting && (
                        <div className="sium-button-container sticky-bottom">
                            <button className="btn sium-button" type="submit">עדכן לומד</button>
                        </div>
                    )}
                    {isSubmitting && (
                        <div className="sium-button-container sticky-bottom">
                            <button type="button" className="btn sium-button">
                                <div className="updateStatusSpinner"></div>
                            </button>
                        </div>
                    )}
                </form>

                <div className="sium-bottom-page margin-top-20">
                    <p><span> © כל הזכויות שמורות לאתר סיום הש"ס</span></p>
                </div>
            </div>
        </div>
    );
};

export default EditUserAher;
